import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useDropdownPosition from '../../hooks/useDropdownPosition';
import useAiSuggestions from '../../hooks/useAiSuggestions';
import { GENERATE_INSTRUCTION_SUGGESTIONS } from '../../lib/graphql';
import SuggestionChips from './SuggestionChips';
import type { AiGenerateAction } from './AiGenerateDropdownButton';

export type QuickActionIcon = 'auto' | 'dice' | 'sparkles' | 'wand';

export interface AiCornerActionsProps {
  onQuickAction?: () => void;
  onMenuAction?: (action: AiGenerateAction, instructions?: string) => void;
  isLoading?: boolean;
  hasContent?: boolean;
  disabled?: boolean;
  showRephraseOptions?: boolean;
  showChatOption?: boolean;
  showMenu?: boolean;
  quickActionIcon?: QuickActionIcon;
  quickActionLabel?: string;
  bookId?: string;
  anchor?: 'input' | 'textarea';
  size?: 'sm' | 'md';
}

function DiceIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      {/* Top face (lightest) */}
      <path
        d="M12 3L4 7L12 11L20 7L12 3Z"
        fill="currentColor"
        fillOpacity="0.2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {/* Left face (medium) */}
      <path
        d="M4 7L4 16L12 20L12 11L4 7Z"
        fill="currentColor"
        fillOpacity="0.45"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {/* Right face (darkest) */}
      <path
        d="M20 7L20 16L12 20L12 11L20 7Z"
        fill="currentColor"
        fillOpacity="0.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {/* Top face pip (1) */}
      <circle cx="12" cy="7" r="0.95" fill="currentColor" />
      {/* Left face pips (2) */}
      <circle cx="6.5" cy="11" r="0.85" fill="currentColor" />
      <circle cx="9.5" cy="17" r="0.85" fill="currentColor" />
      {/* Right face pips (3) */}
      <circle cx="17.5" cy="11" r="0.85" fill="currentColor" />
      <circle cx="16" cy="14" r="0.85" fill="currentColor" />
      <circle cx="14.5" cy="17" r="0.85" fill="currentColor" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 4v4M3 6h4M18 14v4m-2-2h4M11 3l2.5 5.5L19 11l-5.5 2.5L11 19l-2.5-5.5L3 11l5.5-2.5L11 3z" />
    </svg>
  );
}

function WandIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 4V2m0 14v-2M8 9h2m10 0h-2m-.93-4.93l-1.41 1.41M6.34 17.66l-1.41 1.41m0-14.14l1.41 1.41M17.66 17.66l1.41 1.41" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 10l-8 8 2 2 8-8-2-2z" />
    </svg>
  );
}

function MoreDotsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <circle cx="4" cy="10" r="1.6" />
      <circle cx="10" cy="10" r="1.6" />
      <circle cx="16" cy="10" r="1.6" />
    </svg>
  );
}

function Spinner({ className }: { className?: string }) {
  return (
    <svg className={`${className ?? ''} animate-spin`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default function AiCornerActions({
  onQuickAction,
  onMenuAction,
  isLoading = false,
  hasContent = false,
  disabled = false,
  showRephraseOptions = true,
  showChatOption = false,
  showMenu = true,
  quickActionIcon = 'auto',
  quickActionLabel,
  bookId,
  anchor = 'input',
  size = 'md',
}: AiCornerActionsProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [instructionsMode, setInstructionsMode] = useState<'generate' | 'rephrase' | null>(null);
  const [instructions, setInstructions] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useDropdownPosition(dropdownRef, isOpen, 'right');

  const fetchVariables = useMemo(() => ({ bookId: bookId || '' }), [bookId]);
  const {
    suggestions: aiSuggestions,
    isLoading: suggestionsLoading,
    refresh: refreshSuggestions,
  } = useAiSuggestions({
    fetchMutation: GENERATE_INSTRUCTION_SUGGESTIONS,
    fetchVariables,
    responseField: 'generateInstructionSuggestions',
    displayCount: 3,
    fetchCount: 15,
    enabled: instructionsMode !== null && !!bookId,
  });

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setInstructionsMode(null);
        setInstructions('');
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setInstructionsMode(null);
        setInstructions('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (instructionsMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [instructionsMode]);

  const resolvedQuickIcon: QuickActionIcon =
    quickActionIcon === 'auto' ? (hasContent ? 'sparkles' : 'dice') : quickActionIcon;

  const handleQuickClick = () => {
    if (onQuickAction) {
      onQuickAction();
    } else if (onMenuAction) {
      onMenuAction(hasContent && showRephraseOptions ? 'rephrase' : 'generate');
    }
  };

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
    setInstructionsMode(null);
    setInstructions('');
  };

  const handleMenuItem = (action: AiGenerateAction) => {
    if (action === 'generateWithInstructions') {
      setInstructionsMode('generate');
      setInstructions('');
      return;
    }
    if (action === 'rephraseWithInstructions') {
      setInstructionsMode('rephrase');
      setInstructions('');
      return;
    }
    onMenuAction?.(action);
    setIsOpen(false);
  };

  const handleInstructionsSubmit = () => {
    if (instructionsMode === 'generate') {
      onMenuAction?.('generateWithInstructions', instructions);
    } else if (instructionsMode === 'rephrase') {
      onMenuAction?.('rephraseWithInstructions', instructions);
    }
    setIsOpen(false);
    setInstructionsMode(null);
    setInstructions('');
  };

  const handleInstructionsKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleInstructionsSubmit();
    }
  };

  const handleSuggestionChip = (suggestion: string) => {
    setInstructions((prev) => (prev.trim() ? `${prev.trim()}. ${suggestion}` : suggestion));
  };

  const isSm = size === 'sm';
  const positionClass = anchor === 'textarea'
    ? isSm
      ? 'top-1.5 right-1.5'
      : 'top-2.5 right-2.5'
    : isSm
      ? 'top-1/2 -translate-y-1/2 right-1.5'
      : 'top-1/2 -translate-y-1/2 right-2';
  const buttonSizeClass = isSm ? 'w-7 h-7 rounded-md' : 'w-8 h-8 rounded-lg';
  const iconClass = isSm ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const dotsIconClass = isSm ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const quickTooltip =
    quickActionLabel ?? (hasContent ? t('ai.enhanceWithAi') : t('ai.generateWithAi'));

  const renderQuickIcon = () => {
    if (isLoading) return <Spinner className={iconClass} />;
    switch (resolvedQuickIcon) {
      case 'dice':
        return <DiceIcon className={iconClass} />;
      case 'wand':
        return <WandIcon className={iconClass} />;
      case 'sparkles':
      default:
        return <SparklesIcon className={iconClass} />;
    }
  };

  return (
    <div ref={containerRef} className={`absolute ${positionClass} flex items-center gap-0.5 z-10`}>
      <button
        type="button"
        onClick={handleQuickClick}
        disabled={disabled || isLoading}
        title={quickTooltip}
        aria-label={quickTooltip}
        className={`inline-flex items-center justify-center ${buttonSizeClass} text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        {renderQuickIcon()}
      </button>
      {showMenu && (
      <button
        type="button"
        onClick={handleMenuToggle}
        disabled={disabled || isLoading}
        title={t('ai.moreOptions')}
        aria-label={t('ai.moreOptions')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={`inline-flex items-center justify-center ${buttonSizeClass} text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        <MoreDotsIcon className={dotsIconClass} />
      </button>
      )}

      {showMenu && isOpen && (
        <div
          ref={dropdownRef}
          role="menu"
          className="absolute right-0 top-full mt-2 z-30 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 min-w-[280px]"
        >
          {instructionsMode ? (
            <div className="p-3">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                {instructionsMode === 'generate'
                  ? t('ai.instructionsForGenerate')
                  : t('ai.instructionsForRephrase')}
              </label>
              <textarea
                ref={inputRef}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                onKeyDown={handleInstructionsKeyDown}
                placeholder={t('ai.instructionsPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
              {bookId && (
                <SuggestionChips
                  suggestions={aiSuggestions}
                  isLoading={suggestionsLoading}
                  onSelect={handleSuggestionChip}
                  onRefresh={refreshSuggestions}
                />
              )}
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setInstructionsMode(null);
                    setInstructions('');
                  }}
                  className="flex-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleInstructionsSubmit}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                >
                  {t('common.apply')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => handleMenuItem('generate')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <SparklesIcon className="w-4 h-4" />
                {hasContent ? t('ai.regenerate') : t('ai.generate')}
              </button>
              <button
                type="button"
                onClick={() => handleMenuItem('generateWithInstructions')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                {hasContent ? t('ai.regenerateWithInstructions') : t('ai.generateWithInstructions')}
              </button>
              {hasContent && showRephraseOptions && (
                <>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                  <button
                    type="button"
                    onClick={() => handleMenuItem('rephrase')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    {t('ai.rephrase')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMenuItem('rephraseWithInstructions')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    {t('ai.rephraseWithInstructions')}
                  </button>
                </>
              )}
              {showChatOption && (
                <>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                  <button
                    type="button"
                    onClick={() => handleMenuItem('openInChat')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    {t('ai.openInChat')}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
